// src/components/BucksAdminInventory.js

import React, { useState, useEffect, useRef } from 'react';
import '../stylings/BucksAdminInventory.css';
import BucksAdminInventoryProductAddModal from './BucksAdminInventoryProductAddModal';

// → add these right below your other constants/imports
const GET_URL   = 'https://rv0kwp3fuc.execute-api.us-east-1.amazonaws.com/default/bucks-admin-inventory-product-get';
const EDIT_URL  = 'https://rv0kwp3fuc.execute-api.us-east-1.amazonaws.com/default/bucks-admin-inventory-product-edit';
const ADD_URL   = 'https://rv0kwp3fuc.execute-api.us-east-1.amazonaws.com/default/bucks-admin-inventory-product-add';
const DELETE_URL = 'https://rv0kwp3fuc.execute-api.us-east-1.amazonaws.com/default/bucks-admin-inventory-product-delete';

function BucksAdminInventory({ updateMetrics }) {
  // State for inventory data
  const [inventory, setInventory] = useState([]);
  
  // State for search/filter
  const [searchTerm, setSearchTerm] = useState('');
  const [categoryFilter, setCategoryFilter] = useState('all');
  const [statusFilter, setStatusFilter] = useState('all');
  
  // State for modal visibility and data
  const [showModal, setShowModal] = useState(false);
  const [editingItem, setEditingItem] = useState(null);
  
  // State for notifications
  const [notification, setNotification] = useState({ show: false, message: '', type: '' });
  
  // New state for loading and pagination
  const [loading, setLoading] = useState(false);
  const [pagination, setPagination] = useState({
    page: 1,
    page_size: 20,
    total_count: 0,
    total_pages: 0,
    has_next: false,
    has_prev: false
  });
  const searchTimer = useRef(null);
  
  // Sample categories
  const categories = ['Fruit', 'Vegetable', 'Preserves', 'Bakery', 'Dairy', 'Merchandise'];
  
  // Fetch inventory data from API
  const fetchInventory = async (page = 1, searchTerm = '', categoryFilter = 'all', statusFilter = 'all') => {
    setLoading(true);
    try {
      // Construct API URL with query parameters
      let apiUrl = GET_URL;
      
      // Build query parameters
      const params = new URLSearchParams();
      params.append('page', page);
      params.append('page_size', pagination.page_size);
      
      if (searchTerm) {
        params.append('search', searchTerm);
      }
      
      if (categoryFilter !== 'all') {
        params.append('category', categoryFilter);
      }
      
      // Add status filter
      if (statusFilter !== 'all') {
        params.append('status', statusFilter);
      }
      
      // Exclude deleted items by default
      params.append('include_deleted', 'false');
      
      apiUrl += `?${params.toString()}`;
      
      // Fetch data from API
      const response = await fetch(apiUrl);
      
      if (!response.ok) {
        throw new Error(`API request failed with status ${response.status}`);
      }
      
      const data = await response.json();
      
      // Update state with fetched data
      setInventory(data.products);
      setPagination(data.pagination);
      
      // Update dashboard metrics if updateMetrics prop exists
      if (updateMetrics) {
        const totalProducts = data.pagination.total_count;
        const lowStock = data.products.filter(item => item.stock < 10 && item.stock > 0).length;
        // Fix: Include "each" unit products that are manually set to "Out of Stock"
        const outOfStock = data.products.filter(item => 
          item.status === 'Out of Stock' || 
          (item.stock === 0 && item.unit !== 'each')
        ).length;
        const totalValue = data.products.reduce((sum, item) => {
          // Skip negative stock values (for "each" products with infinite stock)
          const stock = item.stock < 0 ? 0 : item.stock;
          return sum + (parseFloat(item.price) * stock);
        }, 0);
        
        updateMetrics({
          totalProducts,
          lowStock,
          outOfStock,
          totalValue,
          recentOrders: 12 // This could be fetched from another API
        });
      }
    } catch (error) {
      console.error('Error fetching inventory:', error);
      setNotification({ 
        show: true, 
        message: `Error loading inventory: ${error.message}`, 
        type: 'error' 
      });
      
      // Set empty inventory on error
      setInventory([]);
    } finally {
      setLoading(false);
    }
  };
  
  // Load inventory data when component mounts
  useEffect(() => {
    fetchInventory(1, searchTerm, categoryFilter, statusFilter);
  }, [updateMetrics]); // eslint-disable-line react-hooks/exhaustive-deps
  
  // Handle search input changes with debounce
  const handleSearchChange = (e) => {
    const value = e.target.value;
    setSearchTerm(value);
    
    // Debounce to avoid too many API calls while typing
    clearTimeout(searchTimer.current);
    searchTimer.current = setTimeout(() => {
      fetchInventory(1, value, categoryFilter, statusFilter);
    }, 500);
  };
  
  // Handle category filter changes
  const handleCategoryChange = (e) => {
    const value = e.target.value;
    setCategoryFilter(value);
    fetchInventory(1, searchTerm, value, statusFilter);
  };
  
  // Add handleStatusChange function
  const handleStatusChange = (e) => {
    const value = e.target.value;
    setStatusFilter(value);
    fetchInventory(1, searchTerm, categoryFilter, value);
  };
  
  // Handle pagination
  const handlePageChange = (newPage) => {
    fetchInventory(newPage, searchTerm, categoryFilter, statusFilter);
  };
  
  // Open modal handlers
  const handleAddClick = () => {
    setEditingItem(null);
    setShowModal(true);
  };
  
  const handleEditClick = async item => {
    try {
      const res = await fetch(`${GET_URL}?product_id=${item.id}&include_images=true`);
      if (!res.ok) throw new Error(`Status ${res.status}`);
      const detail = await res.json();
      
      // Ensure status is properly passed
      const editData = {
        ...detail,
        status: detail.status || item.status // Fallback to the list item status if detailed fetch doesn't include it
      };
      
      setEditingItem(editData);
      setShowModal(true);
    } catch (err) {
      console.error(err);
      setNotification({ show: true, message: `Error loading product: ${err.message}`, type: 'error' });
      setTimeout(() => setNotification({ show: false, message: '', type: '' }), 3000);
    }
  };
  
  const handleCloseModal = () => {
    setShowModal(false);
    setTimeout(() => setEditingItem(null), 300);
  };
  
  // Save or update product
  const handleSaveProduct = async productData => {
    setLoading(true);
    try {
      const endpoint = editingItem ? EDIT_URL : ADD_URL;
      
      // Only auto-calculate status if it's not manually set or if stock is 0
      // Allow manual override for "Out of Stock" even when stock > 0
      if (!productData.status || (parseInt(productData.stock) === 0 && productData.unit !== 'each')) {
        productData.status =
          parseInt(productData.stock) === 0 && productData.unit !== 'each'
            ? 'Out of Stock'
            : parseInt(productData.stock) < 10 && productData.unit !== 'each'
            ? 'Low Stock'
            : 'In Stock';
      }
      
      const res = await fetch(endpoint, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(productData)
      });
      const result = await res.json();
      if (!res.ok) throw new Error(result.error || res.statusText);
      setNotification({
        show: true,
        message: editingItem ? 'Product updated!' : 'Product added!',
        type: 'success'
      });
      handleCloseModal();
      // reload current page
      fetchInventory(pagination.page, searchTerm, categoryFilter, statusFilter);
    } catch (err) {
      console.error(err);
      setNotification({ show: true, message: `Save failed: ${err.message}`, type: 'error' });
    } finally {
      setLoading(false);
      setTimeout(() => setNotification({ show: false, message: '', type: '' }), 3000);
    }
  };
  
  // Delete product (now uses soft delete)
  const handleDeleteItem = async (id, name) => {
    if (!window.confirm(`Delete ${name}?`)) return;
  
    try {
      const res = await fetch(
        `${DELETE_URL}?product_id=${id}`,
        { method: 'DELETE' }
      );
      const payload = await res.json();
      if (!res.ok) throw new Error(payload.error || res.statusText);
  
      // remove from UI - this still makes sense even with soft delete
      setInventory(inv => inv.filter(item => item.id !== id));
      setNotification({ show: true, message: `${name} deleted`, type: 'success' });
    } catch (err) {
      console.error(err);
      
      // Check if this is a foreign key error and provide a better message
      if (err.message && err.message.includes('foreign key constraint fails')) {
        setNotification({ 
          show: true, 
          message: `Cannot delete ${name} because it's referenced in orders. It has been hidden instead.`, 
          type: 'warning' 
        });
        
        // Refresh the page to reflect the soft-deleted state
        fetchInventory(pagination.page, searchTerm, categoryFilter, statusFilter);
      } else {
        setNotification({ show: true, message: `Error deleting: ${err.message}`, type: 'error' });
      }
    } finally {
      setTimeout(() => setNotification({ show: false, message: '', type: '' }), 5000);
    }
  };
  
  
  // Utility functions
  const getStatusClass = status => {
    switch (status) {
      case 'In Stock': return 'status-instock';
      case 'Low Stock': return 'status-lowstock';
      case 'Out of Stock': return 'status-outofstock';
      default: return '';
    }
  };
  
  const formatCurrency = value => `$${parseFloat(value).toFixed(2)}`;
  
  // We don't need to filter inventory locally anymore since API does it
  const filteredInventory = inventory;

  return (
    <div className="bucks-admin-inventory-section">
      {notification.show && (
        <div className={`bucks-admin-notification ${notification.type}`}>
          <span className="bucks-admin-notification-message">{notification.message}</span>
          <button className="bucks-admin-close-notification" onClick={() => setNotification({ show: false, message: '', type: '' })}>×</button>
        </div>
      )}
    
      <div className="bucks-admin-inventory-actions">
        <div className="bucks-admin-search-filter-container">
          <div className="bucks-admin-search-container">
            <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <circle cx="11" cy="11" r="8"></circle>
              <line x1="21" y1="21" x2="16.65" y2="16.65"></line>
            </svg>
            <input
              type="text"
              placeholder="Search products..."
              value={searchTerm}
              onChange={handleSearchChange}
              className="bucks-admin-search-input"
            />
          </div>
          <div className="bucks-admin-filter-container">
            <label htmlFor="category-filter" className="bucks-admin-filter-label">Category:</label>
            <select
              id="category-filter"
              value={categoryFilter}
              onChange={handleCategoryChange}
              className="bucks-admin-category-filter"
            >
              <option value="all">All Categories</option>
              {categories.map(cat => <option key={cat} value={cat}>{cat}</option>)}
            </select>
          </div>
          <div className="bucks-admin-filter-container">
            <label htmlFor="status-filter" className="bucks-admin-filter-label">Status:</label>
            <select
              id="status-filter"
              value={statusFilter}
              onChange={handleStatusChange}
              className="bucks-admin-category-filter"
            >
              <option value="all">All Status</option>
              <option value="In Stock">In Stock</option>
              <option value="Low Stock">Low Stock</option>
              <option value="Out of Stock">Out of Stock</option>
            </select>
          </div>
        </div>
        
        <button className="bucks-admin-add-product-btn" onClick={handleAddClick}>
          Add Product
        </button>
      </div>
      
      {loading ? (
        <div className="bucks-admin-loading">
          <div className="bucks-admin-loading-spinner"></div>
          <span>Loading inventory...</span>
        </div>
      ) : (
        <>
          <div className="bucks-admin-inventory-table-container">
            <table className="bucks-admin-inventory-table">
              <thead>
                <tr>
                  <th>Code</th>
                  <th>Product Name</th>
                  <th>Category</th>
                  <th>Price</th>
                  <th>Stock</th>
                  <th>Status</th>
                  <th>Season</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {filteredInventory.length > 0 ? (
                  filteredInventory.map(item => (
                    <tr key={item.id}>
                      <td>{item.product_code}</td>
                      <td>
                        <div className="bucks-admin-product-name-cell">
                          {item.images && item.images.length > 0 ? (
                            <img 
                              src={item.images[0].image_url} 
                              alt={item.name} 
                              className="bucks-admin-product-thumbnail" 
                            />
                          ) : (
                            <div className="bucks-admin-product-no-image">No Image</div>
                          )}
                          <div className="bucks-admin-product-name-info">
                            <span className="bucks-admin-product-name">{item.name}</span>
                            {item.description && (
                              <span className="bucks-admin-product-description" title={item.description}>
                                {item.description.slice(0, 30)}{item.description.length > 30 ? '…' : ''}
                              </span>
                            )}
                          </div>
                        </div>
                      </td>
                      <td>{item.category}</td>
                      <td>{formatCurrency(item.price)}</td>
                      <td>
                        {item.unit === 'each' ? 
                          'Infinite ' + item.unit : 
                          `${item.stock} ${item.unit}`}
                      </td>
                      <td>
                        <span className={`bucks-admin-status-badge ${getStatusClass(item.status)}`}>
                          {item.status}
                        </span>
                      </td>
                      <td>
                        <span className={`bucks-admin-status-badge ${item.season_status === 'In Season' ? 'status-instock' : 'status-outofstock'}`}>
                          {item.season_status}
                        </span>
                      </td>
                      <td>
                        <div className="bucks-admin-table-actions">
                          <button className="bucks-admin-edit-btn" onClick={() => handleEditClick(item)} title="Edit">
                            <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                              <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"></path>
                              <path d="M18.5 2.5a2.121 2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"></path>
                            </svg>
                          </button>
                          <button className="bucks-admin-delete-btn" onClick={() => handleDeleteItem(item.id, item.name)} title="Delete">
                            <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                              <path d="M3 6h18"></path>
                              <path d="M19 6v14c0 1-1 2-2 2H7c-1 0-2-1-2-2V6"></path>
                              <path d="M8 6V4c0-1 1-2 2-2h4c1 0 2 1 2 2v2"></path>
                            </svg>
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan="8" className="bucks-admin-no-results">
                      No products found matching your search
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
          
          {/* Pagination Controls */}
          {pagination.total_pages > 1 && (
            <div className="bucks-admin-pagination">
              <button 
                className="bucks-admin-pagination-btn" 
                disabled={!pagination.has_prev}
                onClick={() => handlePageChange(pagination.page - 1)}
              >
                Previous
              </button>
              <span className="bucks-admin-pagination-info">
                Page {pagination.page} of {pagination.total_pages}
              </span>
              <button 
                className="bucks-admin-pagination-btn" 
                disabled={!pagination.has_next}
                onClick={() => handlePageChange(pagination.page + 1)}
              >
                Next
              </button>
            </div>
          )}
        </>
      )}
      
      <BucksAdminInventoryProductAddModal
        show={showModal}
        onClose={handleCloseModal}
        onSave={handleSaveProduct}
        editingItem={editingItem}
        categories={categories}
      />
    </div>
  );
}

export default BucksAdminInventory;